/**
 * A marca do clique no chão (techspec-marca-do-clique, Renato 02/11/2026). Substitui o losango amarelo do mouse e o
 * losango que pulsava no destino. Toca uma vez a cada clique de andar, no tile de destino.
 * Quatro estilos, escolhidos em Configurações › Interface (protótipo docs/prototipos/galeria-clique-prototipo.html):
 * setas (padrão), seta que cai, mira e poeira. É preferência do navegador: leitura e gravação em try/catch.
 */
export type MarcaClique = 'setas' | 'seta' | 'mira' | 'poeira';

export const MARCAS_CLIQUE: readonly MarcaClique[] = ['setas', 'seta', 'mira', 'poeira'];

const CHAVE = 'uoidle.jogo.marcaClique';
const PADRAO: MarcaClique = 'setas';

export function lerMarcaClique(): MarcaClique {
  try {
    const v = localStorage.getItem(CHAVE);
    return (MARCAS_CLIQUE as readonly string[]).includes(v ?? '') ? v as MarcaClique : PADRAO;
  } catch {
    return PADRAO;
  }
}

export function gravarMarcaClique(m: MarcaClique) {
  try { localStorage.setItem(CHAVE, m); } catch { /* sem storage: vale o padrão */ }
}

/** Duração de cada estilo, em ms. */
export const DURACAO_MARCA: Readonly<Record<MarcaClique, number>> = { setas: 1200, seta: 1300, mira: 1000, poeira: 900 };

/**
 * Onde a animação para enquanto o personagem ainda está a caminho (progresso de 0 a 1): o último quadro antes de
 * sumir. Quando ele chega, a animação segue daqui até o fim (o sumir).
 */
export const PARADA_MARCA: Readonly<Record<MarcaClique, number>> = { setas: 0.7, seta: 0.75, mira: 0.65, poeira: 0.35 };

type Rgb = readonly [number, number, number];
const OURO = { m: [255, 206, 84] as Rgb, l: [255, 244, 196] as Rgb, d: [122, 86, 12] as Rgb };
/** Meio lado do quadrado do tile no plano do chão (44×22 na tela depois da projeção). */
const T = 15.55;

const rgba = (c: Rgb, a: number) => `rgba(${c[0]},${c[1]},${c[2]},${Math.max(0, Math.min(1, a))})`;
const cl = (v: number) => Math.max(0, Math.min(1, v));
const seg = (p: number, a: number, b: number) => cl((p - a) / (b - a));
const eo = (t: number) => 1 - Math.pow(1 - t, 3);
const fade = (p: number, a: number, b: number) => 1 - seg(p, a, b);
function bounce(t: number){
  const n = 7.5625, d = 2.75;
  if (t < 1 / d) return n * t * t;
  if (t < 2 / d) return n * (t -= 1.5 / d) * t + 0.75;
  if (t < 2.5 / d) return n * (t -= 2.25 / d) * t + 0.9375;
  return n * (t -= 2.625 / d) * t + 0.984375;
}

/** Desenha no plano do chão isométrico: dentro de fn, as unidades são px do jogo vistos de cima. */
function noChao(g: CanvasRenderingContext2D, x: number, y: number, S: number, ang: number, fn: () => void){
  g.save();
  g.translate(x, y);
  g.scale(S, S * 0.5);
  g.rotate(Math.PI / 4 + ang);
  fn();
  g.restore();
}

function setas(g: CanvasRenderingContext2D, x: number, y: number, p: number, S: number){
  const q = eo(seg(p, 0, 0.45));
  const dd = 8 + 30 * (1 - q);
  const a = seg(p, 0, 0.1) * fade(p, 0.7, 1);
  const pul = p > 0.45 ? Math.sin(seg(p, 0.45, 0.7) * Math.PI) * 3 : 0;
  noChao(g, x, y, S, -Math.PI / 4, () => {
    g.shadowColor = rgba(OURO.m, 1);
    g.shadowBlur = 6;
    for (let i = 0; i < 4; i++){
      g.save();
      g.rotate(i * Math.PI / 2);
      g.translate(dd + pul, 0);
      g.beginPath();
      g.moveTo(0, 0); g.lineTo(9, -7); g.lineTo(6, 0); g.lineTo(9, 7);
      g.closePath();
      g.fillStyle = rgba(OURO.m, a);
      g.fill();
      g.lineWidth = 1.4;
      g.strokeStyle = rgba(OURO.d, a);
      g.stroke();
      g.restore();
    }
  });
}

function seta(g: CanvasRenderingContext2D, x: number, y: number, p: number, S: number){
  const h = (1 - bounce(seg(p, 0, 0.5))) * 46 + 5;
  const a = seg(p, 0, 0.08) * fade(p, 0.75, 1);
  noChao(g, x, y, S, 0, () => {
    g.beginPath();
    g.arc(0, 0, 7 + 4 * (1 - h / 51), 0, Math.PI * 2);
    g.fillStyle = `rgba(0,0,0,${0.35 * a})`;
    g.fill();
  });
  g.save();
  g.translate(x, y - h * S);
  g.scale(S, S);
  g.beginPath();
  g.moveTo(0, 0); g.lineTo(-9, -11); g.lineTo(-4, -11); g.lineTo(-4, -23); g.lineTo(4, -23); g.lineTo(4, -11); g.lineTo(9, -11);
  g.closePath();
  const gr = g.createLinearGradient(-9, 0, 9, 0);
  gr.addColorStop(0, rgba(OURO.l, a));
  gr.addColorStop(0.5, rgba(OURO.m, a));
  gr.addColorStop(1, rgba(OURO.d, a));
  g.shadowColor = rgba(OURO.m, 1);
  g.shadowBlur = 6;
  g.fillStyle = gr;
  g.fill();
  g.shadowBlur = 0;
  g.lineWidth = 1.3;
  g.strokeStyle = rgba(OURO.d, a);
  g.stroke();
  g.restore();
}

function mira(g: CanvasRenderingContext2D, x: number, y: number, p: number, S: number){
  const k = 1 + 1.5 * (1 - eo(seg(p, 0, 0.35)));
  const a = seg(p, 0, 0.1) * fade(p, 0.65, 1);
  noChao(g, x, y, S, 0, () => {
    const h = T * k, L = 7;
    g.shadowColor = rgba(OURO.m, 1);
    g.shadowBlur = 6;
    g.lineCap = 'round';
    for (const [sx, sy] of [[1, 1], [1, -1], [-1, 1], [-1, -1]]){
      g.beginPath();
      g.moveTo(sx * h, sy * (h - L)); g.lineTo(sx * h, sy * h); g.lineTo(sx * (h - L), sy * h);
      g.strokeStyle = rgba(OURO.d, a); g.lineWidth = 4.5; g.stroke();
      g.strokeStyle = rgba(OURO.l, a); g.lineWidth = 2; g.stroke();
    }
    g.beginPath();
    g.arc(0, 0, 2.6 * seg(p, 0.3, 0.45), 0, Math.PI * 2);
    g.fillStyle = rgba(OURO.l, a);
    g.fill();
  });
}

function poeira(g: CanvasRenderingContext2D, x: number, y: number, p: number, S: number){
  const q = eo(p);
  const a = (1 - seg(p, 0.15, 1)) * 0.7;
  for (let i = 0; i < 7; i++){
    const an = i * Math.PI * 2 / 7 + 0.3;
    const r = 4 + 14 * q;
    const px = x + Math.cos(an) * r * S, py = y + Math.sin(an) * r * 0.5 * S - q * 6 * S;
    const R = (3.5 + 4 * q) * S;
    const rg = g.createRadialGradient(px, py, 0, px, py, R);
    rg.addColorStop(0, `rgba(196,176,140,${a})`);
    rg.addColorStop(1, 'rgba(196,176,140,0)');
    g.fillStyle = rg;
    g.beginPath();
    g.arc(px, py, R, 0, Math.PI * 2);
    g.fill();
  }
}

const DESENHO: Readonly<Record<MarcaClique, typeof setas>> = { setas, seta, mira, poeira };

/**
 * Desenha a marca no centro do tile (x, y na tela). `p` é o progresso de 0 a 1; `S` a escala (zoom × tile/44).
 */
export function desenharMarcaClique(g: CanvasRenderingContext2D, tipo: MarcaClique, x: number, y: number, p: number, S: number){
  g.save();
  DESENHO[tipo](g, x, y, cl(p), S);
  g.restore();
}
